import React, { useState, useEffect } from 'react';
import { isKatakana } from '@this/src/util';
import type { ReservationInfo, SelectOption, TravelerFields, TravelerFormValues } from '../../api/reservationInfo';
import TravelerSection from './ReservationInfoFormTravelerSection';

type FormValues = {
  finalDestination: string;
  purpose: string;
  internalNumber: string;
  chargingDepartmentId: number | null;
  projectId: number | null;
  traveler: TravelerFormValues | null;
};

type Props = {
  reservationInfo: ReservationInfo;
  onSubmit: (values: FormValues) => void;
  isSubmitting?: boolean;
  editable?: boolean;
};

const buildInitialTravelerValues = (travelerFields: TravelerFields | null): TravelerFormValues | null => {
  if (!travelerFields) return null;
  const { currentValues } = travelerFields;
  return {
    flightFirstName: currentValues.flightFirstName || '',
    flightLastName: currentValues.flightLastName || '',
    flightFirstNameKana: currentValues.flightFirstNameKana || '',
    flightLastNameKana: currentValues.flightLastNameKana || '',
    flightFirstNameRoman: currentValues.flightFirstNameRoman || '',
    flightLastNameRoman: currentValues.flightLastNameRoman || '',
    flightBirthday: currentValues.flightBirthday || '',
    flightGender: currentValues.flightGender || '',
    flightTel: currentValues.flightTel || '',
    airSeat: currentValues.airSeat || ''
  };
};

const ReservationInfoForm = ({ reservationInfo, onSubmit, isSubmitting = false, editable = true }: Props) => {
  const { currentValues, settingFlags, options, travelerFields } = reservationInfo;

  const [formValues, setFormValues] = useState<FormValues>({
    finalDestination: currentValues.finalDestination || '',
    purpose: currentValues.purpose || '',
    internalNumber: currentValues.internalNumber || '',
    chargingDepartmentId: currentValues.chargingDepartmentId,
    projectId: currentValues.projectId,
    traveler: buildInitialTravelerValues(travelerFields)
  });

  useEffect(() => {
    setFormValues(prev => ({
      ...prev,
      traveler: buildInitialTravelerValues(travelerFields)
    }));
  }, [travelerFields]);

  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const [travelerErrors, setTravelerErrors] = useState<Partial<Record<keyof TravelerFormValues, string>>>({});

  const validateTravelerFields = (): Partial<Record<keyof TravelerFormValues, string>> => {
    const newErrors: Partial<Record<keyof TravelerFormValues, string>> = {};
    if (!travelerFields || !formValues.traveler) return newErrors;

    const { required } = travelerFields;
    const traveler = formValues.traveler;

    if (required.includes('flight_first_name_kana') || required.includes('flight_last_name_kana')) {
      if (!traveler.flightLastNameKana.trim()) {
        newErrors.flightLastNameKana = '姓（カナ）を入力してください';
      } else if (!isKatakana(traveler.flightLastNameKana.trim())) {
        newErrors.flightLastNameKana = 'カタカナで入力してください';
      }
      if (!traveler.flightFirstNameKana.trim()) {
        newErrors.flightFirstNameKana = '名（カナ）を入力してください';
      } else if (!isKatakana(traveler.flightFirstNameKana.trim())) {
        newErrors.flightFirstNameKana = 'カタカナで入力してください';
      }
    }
    if (required.includes('flight_birthday') && !traveler.flightBirthday) {
      newErrors.flightBirthday = '生年月日を入力してください';
    }
    if (required.includes('flight_gender') && !traveler.flightGender) {
      newErrors.flightGender = '性別を選択してください';
    }
    if (required.includes('flight_tel') && !traveler.flightTel.trim()) {
      newErrors.flightTel = '緊急連絡先を入力してください';
    }

    return newErrors;
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormValues, string>> = {};

    if (settingFlags.showDestinationBusinessTrip && settingFlags.isDestinationBusinessTripRequired) {
      if (!formValues.finalDestination.trim()) {
        newErrors.finalDestination = '出張先を入力してください';
      }
    }

    if (settingFlags.showPurposeBusinessTrip && settingFlags.isPurposeBusinessTripRequired) {
      if (!formValues.purpose.trim()) {
        newErrors.purpose = '出張の目的を入力してください';
      }
    }

    if (settingFlags.showInternalNumber && settingFlags.isInternalNumberRequired) {
      if (!formValues.internalNumber.trim()) {
        newErrors.internalNumber = '社内管理番号を入力してください';
      }
    }

    if (settingFlags.showChargingDepartment && settingFlags.isChargingDepartmentRequired) {
      if (!formValues.chargingDepartmentId) {
        newErrors.chargingDepartmentId = '費用負担部署を選択してください';
      }
    }

    if (settingFlags.showProjectName && settingFlags.isProjectNameRequired) {
      if (!formValues.projectId) {
        newErrors.projectId = 'プロジェクトを選択してください';
      }
    }

    const newTravelerErrors = validateTravelerFields();

    setErrors(newErrors);
    setTravelerErrors(newTravelerErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(newTravelerErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formValues);
    }
  };

  const handleTextChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFormValues(prev => ({ ...prev, [field]: newValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value ? Number(e.target.value) : null;
    setFormValues(prev => ({ ...prev, [field]: newValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleTravelerTextChange =
    (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setFormValues(prev => ({
        ...prev,
        traveler: prev.traveler ? { ...prev.traveler, [field]: newValue } : null
      }));
      if (travelerErrors[field]) {
        setTravelerErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleTravelerSelectChange =
    (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      setFormValues(prev => ({
        ...prev,
        traveler: prev.traveler ? { ...prev.traveler, [field]: newValue } : null
      }));
      if (travelerErrors[field]) {
        setTravelerErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const renderTextInput = (
    field: keyof FormValues,
    label: string,
    placeholder: string,
    isRequired: boolean,
    isShown: boolean
  ) => {
    if (!isShown) return null;
    const inputId = `reservation-info-${field}`;
    return (
      <div style={styles.formGroup}>
        <label htmlFor={inputId} style={styles.label}>
          {label}
          {isRequired && <span style={styles.required}>*</span>}
        </label>
        <input
          id={inputId}
          type="text"
          style={{
            ...styles.input,
            ...(errors[field] ? styles.inputError : {})
          }}
          placeholder={placeholder}
          value={formValues[field] as string}
          onChange={handleTextChange(field)}
          disabled={isSubmitting || !editable}
        />
        {errors[field] && <span style={styles.errorText}>{errors[field]}</span>}
      </div>
    );
  };

  const renderSelectInput = (
    field: keyof FormValues,
    label: string,
    optionList: SelectOption[],
    isRequired: boolean,
    isShown: boolean
  ) => {
    if (!isShown) return null;
    const selectId = `reservation-info-${field}`;
    return (
      <div style={styles.formGroup}>
        <label htmlFor={selectId} style={styles.label}>
          {label}
          {isRequired && <span style={styles.required}>*</span>}
        </label>
        <select
          id={selectId}
          style={{
            ...styles.select,
            ...(errors[field] ? styles.inputError : {})
          }}
          value={formValues[field] as number | ''}
          onChange={handleSelectChange(field)}
          disabled={isSubmitting || !editable}
        >
          <option value="">選択してください</option>
          {optionList.map(opt => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
        {errors[field] && <span style={styles.errorText}>{errors[field]}</span>}
      </div>
    );
  };

  const hasVisibleFields =
    settingFlags.showDestinationBusinessTrip ||
    settingFlags.showPurposeBusinessTrip ||
    settingFlags.showInternalNumber ||
    settingFlags.showChargingDepartment ||
    settingFlags.showProjectName ||
    travelerFields !== null;

  if (!hasVisibleFields) {
    return null;
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>出張申請に関する情報</h3>
      <form onSubmit={handleSubmit}>
        {renderTextInput(
          'finalDestination',
          '出張先',
          '例：大阪',
          settingFlags.isDestinationBusinessTripRequired,
          settingFlags.showDestinationBusinessTrip
        )}
        {renderTextInput(
          'purpose',
          '出張の目的',
          '例：クライアント訪問',
          settingFlags.isPurposeBusinessTripRequired,
          settingFlags.showPurposeBusinessTrip
        )}
        {renderTextInput(
          'internalNumber',
          '社内管理番号',
          '例：20250116-001',
          settingFlags.isInternalNumberRequired,
          settingFlags.showInternalNumber
        )}
        {renderSelectInput(
          'chargingDepartmentId',
          '費用負担部署',
          options.departments,
          settingFlags.isChargingDepartmentRequired,
          settingFlags.showChargingDepartment
        )}
        {renderSelectInput(
          'projectId',
          'プロジェクト（案件）',
          options.projects,
          settingFlags.isProjectNameRequired,
          settingFlags.showProjectName
        )}
        {travelerFields && formValues.traveler && (
          <TravelerSection
            travelerFields={travelerFields}
            traveler={formValues.traveler}
            travelerErrors={travelerErrors}
            onTextChange={handleTravelerTextChange}
            onSelectChange={handleTravelerSelectChange}
            disabled={isSubmitting || !editable}
          />
        )}
        <button
          type="submit"
          style={{
            ...styles.submitButton,
            ...(!editable ? styles.submitButtonDisabled : {})
          }}
          disabled={isSubmitting || !editable}
        >
          {!editable ? '送信済み' : isSubmitting ? '送信中...' : '送信'}
        </button>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '20px',
    maxWidth: '300px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '16px',
    marginTop: 0
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '6px',
    color: '#333'
  },
  required: {
    color: '#e74c3c',
    marginLeft: '4px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  inputError: {
    borderColor: '#e74c3c'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  submitButton: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '8px'
  },
  submitButtonDisabled: {
    backgroundColor: '#6c757d',
    cursor: 'not-allowed'
  }
};

export default ReservationInfoForm;
export type { FormValues };
